import * as React from "react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { AnimatePresence, motion } from "framer-motion";
import { useColumnAnalysisConversationMutation } from "@/modules/snapshots/businessReports.api";
import { ChatMessageBubble } from "@/components/chat/ChatMessageBubble";
import { StreamingArtefactMarkdown } from "@/components/feature/markdown/StreamingArtefactMarkdown";
import { TypingIndicator } from "@/components/chat/TypingIndicator";
import { ChatInput } from "@/components/chat/ChatInput";
import { ScrollableChat } from "@/components/feature/chat/ScrollableChat";
import { CHAT_ANIMATION, MESSAGE_STYLES } from "@/components/chat/constants";
import type { ChatMessage } from "@/components/chat/types";
import { MessageSquare } from "lucide-react";
import { useColumnAnalysisStream } from "./hooks/useColumnAnalysisStream";
import { useArtefactExtraction } from "@/hooks/useArtefactExtraction";

interface ColumnAnalysisMessageProps {
  message: ChatMessage;
  actions?: Record<string, boolean>;
  hideActions?: boolean;
}

const ColumnAnalysisMessage: React.FC<ColumnAnalysisMessageProps> = ({ message, actions, hideActions }) => {
  const [processedContent, inlineArtefacts] = useArtefactExtraction(message.content);
  const processedMessage = React.useMemo(
    () => ({ ...message, content: processedContent }),
    [message, processedContent],
  );

  return (
    <ChatMessageBubble
      message={processedMessage}
      actions={actions}
      hideActions={hideActions}
      inlineArtefacts={inlineArtefacts}
    />
  );
};

interface StreamingChatMessageProps {
  content: string;
  isStreaming: boolean;
}

const StreamingChatMessage: React.FC<StreamingChatMessageProps> = ({ content, isStreaming }) => {
  const [processedContent, inlineArtefacts] = useArtefactExtraction(content);

  return (
    <motion.div
      className="mb-3 w-full"
      initial={CHAT_ANIMATION.fadeIn}
      animate={CHAT_ANIMATION.visible}
      exit={CHAT_ANIMATION.fadeOut}
      transition={CHAT_ANIMATION.spring}
    >
      <div className={MESSAGE_STYLES.ai}>
        <StreamingArtefactMarkdown content={processedContent} artefacts={inlineArtefacts} />
        <AnimatePresence>
          {isStreaming && (
            <div className="text-coral">
              <TypingIndicator />
            </div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

interface Props {
  businessReportId: string;
  snapshotCollectionId: string;
  subcollectionId?: string;
  fieldName: string;
  contractFamilyProjection?: boolean;
}

export const Conversation: React.FC<Props> = ({
  businessReportId,
  snapshotCollectionId,
  subcollectionId,
  fieldName,
  contractFamilyProjection = false,
}) => {
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = React.useState<string | null>(null);
  const [isStreamActive, setIsStreamActive] = React.useState(false);
  const [sendMessage] = useColumnAnalysisConversationMutation();

  const handleComplete = React.useCallback(
    (content: string) => {
      setMessages(prev => [
        ...prev,
        {
          id: conversationId ?? crypto.randomUUID(),
          type: "ai",
          content,
        },
      ]);
      setIsStreamActive(false);
      setConversationId(null);
    },
    [conversationId],
  );

  const { content: streamContent, isStreaming } = useColumnAnalysisStream({
    conversationId,
    onComplete: handleComplete,
  });

  const streamingMessage: ChatMessage | null =
    isStreaming && conversationId
      ? { id: `stream-${conversationId}`, type: "ai", content: streamContent, completed: false }
      : null;

  const handleSend = async (text: string) => {
    const userMsg: ChatMessage = { id: crypto.randomUUID(), type: "human", content: text };
    setMessages(prev => [...prev, userMsg]);
    setIsStreamActive(true);

    const newConversationId = crypto.randomUUID();
    setConversationId(newConversationId);

    const conversationHistory = messages.map(m => ({
      role: m.type === "human" ? ("user" as const) : ("assistant" as const),
      content: m.content,
    }));

    try {
      await sendMessage({
        business_report_id: businessReportId,
        snapshot_collection_id: snapshotCollectionId,
        subcollection_id: subcollectionId ?? null,
        field_name: fieldName,
        query: text,
        conversation_history: conversationHistory,
        conversation_id: newConversationId,
        contract_family_projection: contractFamilyProjection,
      }).unwrap();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to send message. Please try again.");
      setIsStreamActive(false);
      setConversationId(null);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {messages.length === 0 && !isStreamActive && (
        <div className="flex flex-col items-center justify-center gap-4 p-8">
          <MessageSquare className="h-12 w-12 text-gray-400" />
          <p className="text-center text-sm text-gray-600">Chat with AI about the &quot;{fieldName}&quot; column</p>
        </div>
      )}

      <div className="min-h-0 flex-1">
        <ScrollableChat className="px-4 py-3">
          <AnimatePresence mode="popLayout">
            {messages.map(message => (
              <ColumnAnalysisMessage
                key={message.id}
                message={message}
                actions={message.type === "ai" ? { showCopy: true } : {}}
                hideActions={message.type === "human"}
              />
            ))}
            {streamingMessage && (
              <StreamingChatMessage key={streamingMessage.id} content={streamingMessage.content} isStreaming />
            )}
          </AnimatePresence>
        </ScrollableChat>
      </div>

      <div className="pb-3 pl-4">
        <ChatInput
          onSendMessage={handleSend}
          isStreaming={isStreamActive}
          isReady={true}
          placeholder="Ask a question..."
          compact
        />
      </div>
    </div>
  );
};
